import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FeedbackContext } from '@cvt/contexts';
import { Score, getScoreData, MaxValueByType } from '@modules/Analysis/components/Feedback/Score';
import { EMOTIONAL_ARCS } from '../../../data/emotionalArcs';


const EMOTIONAL_ARC_CATEGORY = 'Emotional Arc Execution';

type StructuralOverviewTabProps = {
  story: Stories.Story;
};

export const OverviewTab: React.FC<StructuralOverviewTabProps> = ({ story }) => {
  const analysisData = story.structuralFeedback;
  const { triggerFeedback } = React.useContext(FeedbackContext);

  const emotionalArcPerformance = analysisData?.categories?.find(({ name }) => (
    name === EMOTIONAL_ARC_CATEGORY
  ));

  const arcDots = useMemo(() => {
    if (!emotionalArcPerformance) return '';

    const maxValue = MaxValueByType.arc;
    const score = Math.max(0, Math.min(maxValue, Math.round(emotionalArcPerformance.stars)));
    const filled = '●'.repeat(score);
    const empty = '○'.repeat(maxValue - score);
    return `${filled}${empty}`;
  }, [emotionalArcPerformance]);

  const copyToClipboard = useCallback(
    async (text: string, label: string) => {
      try {
        await navigator.clipboard.writeText(text);
        triggerFeedback({
          severity: 'success',
          message: `${label} copied to clipboard.`,
        });
      } catch {
        triggerFeedback({
          severity: 'warning',
          message: `Unable to copy ${label}.`,
        });
      }
    },
    [triggerFeedback],
  );

  const copyOverview = useCallback(() => {
    if (!analysisData) return;

    const { progress: structureProgress } = getScoreData('structural', analysisData.scoreStructure);
    const { progress: arcProgress } = getScoreData('arc', analysisData.scoreArc);

    const overview = `
Story Analysis Overview - ${story.title ?? 'Untitled'}
Performance Score: ${structureProgress} (${arcProgress})
Tier: ${analysisData.tier}

Top Strengths:
${(analysisData.strengths ?? []).map((s: string) => `• ${s}`).join('\n')}

Key Opportunities:
${(analysisData.topOpportunities ?? []).map((o: string) => `• ${o}`).join('\n')}
`.trim();

    copyToClipboard(overview, 'Overview');
  }, [analysisData, story.title, copyToClipboard]);

  const selectedEmotionalArc = useMemo(() => (
    EMOTIONAL_ARCS.find(({ code }) => story.emotionalArc === code)
  ), [story.emotionalArc]);

  if (!analysisData) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
              No structural feedback available yet.
          </Typography>
          <Box mt={2}>
            <Button variant="outlined" disabled startIcon={<ContentCopyIcon fontSize="small"/>}>
                Copy Overview
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            <Score
              type="structural"
              score={analysisData.scoreStructure}
              render={({ progress }) => (
                `📜 Structural Score ${progress}`
              )}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
              Tier: {analysisData.tier}
          </Typography>
        </Box>

        <Box textAlign="right">
          <Typography variant="subtitle1" fontWeight={600}>
            <Score
              type="arc"
              score={emotionalArcPerformance?.stars || 0}
              render={({ progress }) => (
                <>Arc Strength: {arcDots} ({progress})</>
              )}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedEmotionalArc && (
              <>
                {selectedEmotionalArc.name}<br/>
                {selectedEmotionalArc.emotionalJourney}<br/>
              </>
            )}
              Arc Shape: {analysisData.arcShape}
          </Typography>
        </Box>
      </Box>

      <Card variant="outlined">
        <CardHeader title={<Typography variant="subtitle1">Top 3 Opportunities</Typography>}/>
        <CardContent>
          <List dense disablePadding>
            {(analysisData.topOpportunities ?? []).map((opportunity: string, index: number) => (
              <ListItem key={index} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 28, pt: '2px' }}>
                  <Typography color="primary" fontWeight={600}>
                    {index + 1}.
                  </Typography>
                </ListItemIcon>
                <ListItemText primary={opportunity}/>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader title={<Typography variant="subtitle1">Top 3 Strengths</Typography>}/>
        <CardContent>
          <List dense disablePadding>
            {(analysisData.strengths ?? []).slice(0, 3).map((strength: string, index: number) => (
              <ListItem key={index} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 24, pt: '2px' }}>
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      bgcolor: 'success.main',
                      borderRadius: '50%',
                      display: 'inline-block',
                    }}
                  />
                </ListItemIcon>
                <ListItemText primary={strength}/>
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={copyOverview} startIcon={<ContentCopyIcon fontSize="small"/>}>
            Copy Overview
        </Button>
      </Stack>
    </Stack>
  );
};